import * as React from 'react'
import loadable from '@loadable/component'

const ChevronRight = loadable(
  () => import('@thg-commerce/gravity-icons/src/components/ChevronRight'),
  { ssr: true, fallback: <div style={{ width: 24, height: 24 }} /> },
)
const SvgIcon = loadable(
  () => import('@thg-commerce/gravity-icons/src/components/SvgIcon'),
  { ssr: true, fallback: <div style={{ width: 24, height: 24 }} /> },
)
import { Text, spacing, mq } from '@thg-commerce/gravity-theme'

import { NavigationLink } from '../NavigationTree'
import { styled, css, useTheme } from '../../theme'

export interface NavigationTextItemProps {
  hasChildren: boolean
  text: string
  link?: NavigationLink
  onClick?: () => void
  'aria-label'?: string
  shouldFocus?: boolean
  isAmp?: boolean
  currentLevel?: number
}

export const NavigationTextItem = (props: NavigationTextItemProps) => {
  const theme = useTheme()

  const highlightStyles = css`
    background-color: ${(props) =>
      props.theme.patterns.header.navigation.mobileLinkBackgroundHover};
    ${(props) =>
      Text(
        props.theme.patterns.header.navigation.mobileLinkHover.entry,
        props.theme.patterns.header.navigation.mobileLinkHover.style,
      )}
    text-transform: ${(props) =>
      props.theme.patterns.header.navigation.mobileLinkHover.transform};
    text-decoration: ${(props) =>
      props.theme.patterns.header.navigation.mobileLinkHover.textDecoration};
    color: ${(props) =>
      props.theme.patterns.header.navigation.mobileLinkHover.textColor};
  `

  const navigationLinkCommonStyles = css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 40px;
    width: 100%;
    ${(props) =>
      Text(
        props.theme.patterns.header.navigation.mobileLinkFont.entry,
        props.theme.patterns.header.navigation.mobileLinkFont.style,
      )};
    padding: 0 ${spacing(1)};
    border: 2px solid transparent;
    text-decoration: ${(props) =>
      props.theme.patterns.header.navigation.mobileLinkFont.textDecoration};
    text-transform: ${(props) =>
      props.theme.patterns.header.navigation.mobileLinkFont.transform};

    @media (hover: none) {
      &:active {
        ${highlightStyles}
      }
    }

    @media (hover: hover) {
      &:hover {
        ${highlightStyles}
      }
    }
    ${(props) => mq(props.theme.breakpointUtils.map, 'lg')} {
      text-transform: ${(props) =>
        props.theme.patterns.header.navigation.mobileLinkFont.transform};
    }

    &:focus {
      outline: none;
      border-color: ${(props) => props.theme.colors.palette.brand.base};
    }

    ${(props) => mq(props.theme.breakpointUtils.map, 'lg')} {
      ${(props) =>
        Text(
          props.theme.patterns.header.navigation.mobileLinkFont.entry,
          props.theme.patterns.header.navigation.mobileLinkFont.style,
        )}
      text-transform: ${(props) =>
        props.theme.patterns.header.navigation.mobileLinkFont.transform};
      text-decoration: ${(props) =>
        props.theme.patterns.header.navigation.mobileLinkFont.textDecoration};
      color: ${(props) =>
        props.theme.patterns.header.navigation.mobileLinkFont.textColor};
    }
  `

  const NavigationLinkWithChildren = styled.button`
    ${navigationLinkCommonStyles}
  `

  const NavigationLink = styled.a`
    ${navigationLinkCommonStyles}
  `

  const StyledChevronRight = styled(ChevronRight)`
    width: 24px;
  `

  const IconWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 100%;
  `

  const linkAttributes = {
    href: props?.link?.url || undefined,
    target: (props?.link?.openExternally && '_BLANK') || undefined,
    rel: `${props.link?.noFollow ? 'nofollow' : ''} ${
      props.link?.noIndex ? 'noindex' : ''
    }`,
  }

  const AmpOpenSubmenu = (props) => (
    <button amp-nested-submenu-open="true" {...props}>
      {props.children}
    </button>
  )

  const StyledAmpOpenSubmenu = styled(AmpOpenSubmenu)`
    ${navigationLinkCommonStyles}
  `

  return props.hasChildren ? (
    props.isAmp ? (
      <StyledAmpOpenSubmenu
        aria-label={props['aria-label']}
        onClick={props.onClick}
      >
        <span>{props.text}</span>
        <IconWrapper>
          {theme.patterns.header.flyout.chevronRightIcon.svgPath ? (
            <SvgIcon
              xmlns="http://www.w3.org/2000/svg"
              viewBox={theme.patterns.header.flyout.chevronRightIcon.viewBox}
              width={theme.patterns.header.flyout.chevronRightIcon.width}
              height={theme.patterns.header.flyout.chevronRightIcon.height}
            >
              <path
                d={theme.patterns.header.flyout.chevronRightIcon.svgPath}
                fill-rule="evenodd"
              ></path>
            </SvgIcon>
          ) : (
            <StyledChevronRight />
          )}
        </IconWrapper>
      </StyledAmpOpenSubmenu>
    ) : (
      <NavigationLinkWithChildren
        aria-label={props['aria-label']}
        onClick={props.onClick}
        data-should-focus={props.shouldFocus}
      >
        <span>{props.text}</span>
        <IconWrapper>
          {theme.patterns.header.flyout.chevronRightIcon.svgPath ? (
            <SvgIcon
              xmlns="http://www.w3.org/2000/svg"
              viewBox={theme.patterns.header.flyout.chevronRightIcon.viewBox}
              width={theme.patterns.header.flyout.chevronRightIcon.width}
              height={theme.patterns.header.flyout.chevronRightIcon.height}
            >
              <path
                d={theme.patterns.header.flyout.chevronRightIcon.svgPath}
                fill-rule="evenodd"
              ></path>
            </SvgIcon>
          ) : (
            <StyledChevronRight />
          )}
        </IconWrapper>
      </NavigationLinkWithChildren>
    )
  ) : (
    <NavigationLink {...linkAttributes} data-should-focus={props.shouldFocus}>
      <span>{props.text}</span>
    </NavigationLink>
  )
}
